import React, { useMemo, useState, useCallback } from 'react';
import programRowStyles from './ProgramRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ProgramRow from './ProgramRow';
import ListContent from '../composites/ListContent';
import AddProgramDialog from './AddProgramDialog';
import EditProgramTemplate from './EditProgramTemplate';
import { GymType } from '../../constants';
import { getDefaultProgram } from '../../utils';
import { useSessionData, useUserAction } from '../../providers';
// Removed: EditTemplate is now handled internally by EditProgramTemplate

import type { Program, WithOptionalId } from '../../types';

const programListTitles = [
  'Program Name',
  'Created By',
  '1X1 CLIENTS',
  'WEEKS',
  'LAST EDIT', // Added new column
  'Options',
];

const Programs: React.FC = () => {
  const { createProgram, updateProgramAndRefreshPrograms, deleteProgram } =
    useUserAction();
  const { loginTrainer, gym, programs } = useSessionData();
  const isGymOfTypeSchool = gym?.type === GymType.School;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
  // removed template modal management
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<Program> | null>(null);
  // Template editing is handled inside EditProgramTemplate via DrillStack
  // No need to hide a parent wrapper with ContentDrill
  const filteredPrograms = useMemo(() => {
    if (!Object.values(programs).length) return [];
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return Object.values(programs)
      .filter(({ name }) => {
        if (!normalizedQuery) return true;
        return name.toLowerCase().includes(normalizedQuery);
      })
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [searchQuery, programs]);

  // Moved above early return to satisfy rules-of-hooks
  const handleSubmitEditProgramTemplate = useCallback(
    async (newProgramData: WithOptionalId<Program>): Promise<boolean> => {
      if (!loginTrainer) return false;
      const success = newProgramData.id
        ? await updateProgramAndRefreshPrograms(newProgramData as Program)
        : (await createProgram(newProgramData, loginTrainer.id)).success;
      if (!success) {
        return false;
      }
      setIsAddProgramModalOpen(false);
      return true;
    },
    [updateProgramAndRefreshPrograms, createProgram, loginTrainer],
  );

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<SummarizeOutlinedIcon />}
        text="No Programs for logged in role."
      />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<SummarizeOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Program',
        onClick: () =>
          isGymOfTypeSchool
            ? CreateDefaultProgramAndMoveToEdit()
            : setIsAddProgramModalOpen(true),
      }}
      text="No Programs Have Been Added"
    />
  );

  const CreateDefaultProgramAndMoveToEdit = () => {
    const newDefaultProgram = getDefaultProgram(loginTrainer.id);
    setEditedProgram(newDefaultProgram);
  };

  const renderAddProgramDialog = () => (
    <AddProgramDialog
      title="Create A Program"
      trainerId={loginTrainer.id}
      addProgram={setEditedProgram}
      onClose={() => setIsAddProgramModalOpen(false)}
      isOpen={isAddProgramModalOpen}
    />
  );

  const removeProgram = async (program: Program) => {
    await deleteProgram(program);
  };

  const duplicateProgram = async (program: Program) => {
    const newProgramData = {
      ...program,
      name: `${program.name} - Copy`,
      id: undefined,
      trainerId: loginTrainer.id,
    };
    await createProgram(newProgramData, loginTrainer.id);
  };

  // Removed: routine/template editing now lives inside EditProgram

  const renderContent = () => {
    if (editedProgram) return null;

    return (
      <ListContent
        items={filteredPrograms}
        listHeaderClassName={programRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={programListTitles}
        ItemComponent={({ data }) => (
          <ProgramRow
            program={data}
            deleteProgram={removeProgram}
            allowDelete={loginTrainer.id === data.trainerId}
            duplicateProgram={duplicateProgram}
            openEditProgramModal={() => setEditedProgram(data)}
            selected={selectedProgram === data.id}
            onSelect={() => setSelectedProgram(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  // Removed: EditTemplate drill-in

  const handleCloseEditProgramTemplate = () => {
    setEditedProgram(null);
  };

  const getDrillInContentEditProgramTemplate = () => {
    if (!editedProgram) return null;
    return (
      <EditProgramTemplate
        program={editedProgram}
        onSubmit={handleSubmitEditProgramTemplate}
        onClose={handleCloseEditProgramTemplate}
        // setHideWrapper removed with ContentDrill
        proceedAfterSave={true}
      />
    );
  };

  const getDrillInContent = () => {
    if (editedProgram) {
      return getDrillInContentEditProgramTemplate();
    }
    return null;
  };

  const handleDashboardBack = () => {
    if (editedProgram) {
      handleCloseEditProgramTemplate();
    }
  };

  const getSearchPlaceholder = () => {
    if (!filteredPrograms.length) return '';
    return 'Search for Programs';
  };

  return (
    <ContentDrill
      title={editedProgram ? 'Edit Program' : 'Programs'}
      onBack={editedProgram ? () => handleDashboardBack() : undefined}
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Program',
          onClick: () =>
            isGymOfTypeSchool
              ? CreateDefaultProgramAndMoveToEdit()
              : setIsAddProgramModalOpen(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
      isDrilledIn={Boolean(editedProgram)}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Programs;
